import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, IconButton, Paper } from '@mui/material'
import { dateFormat, Format } from '../../utility/date-format'
import { Preloader } from '../common'
import RefreshIcon from '@mui/icons-material/Refresh'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { API } from '../../api'
import { TADBackground } from '../../type/ad-management'

type Props = {
  onRefresh?: () => void | Promise<void>
  refreshTrigger?: number
}

const isUpdating = (background: TADBackground | null) => !background || background.TasksQtyToProcess > 0

export const AdCacheStatus = ({ onRefresh, refreshTrigger }: Props = {}) => {
  const [background, setBackground] = useState<TADBackground | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  const fetchBackground = useCallback(async (withPageRefresh = false) => {
    try {
      setIsLoading(true)
      const data = await API.ADManagement.background()
      setBackground(data)
      if (withPageRefresh && onRefreshRef.current) {
        await onRefreshRef.current()
      }
    } catch {
      // silently fail — cache status is informational
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchBackground(false)
  }, [fetchBackground])

  useEffect(() => {
    if (refreshTrigger) {
      void fetchBackground(false)
    }
  }, [refreshTrigger, fetchBackground])

  const updating = isUpdating(background)

  const renderIcon = () => {
    if (updating) {
      return <WarningAmberIcon sx={{ color: 'var(--warning, #f59e0b)', fontSize: 20 }} />
    }
    return <TaskAltIcon sx={{ color: 'var(--success, #22c55e)', fontSize: 20 }} />
  }

  const baseStatus = background?.CacheStatus ?? ''
  const taskLabel = background?.TasksQtyToProcess === 1 ? 'task' : 'tasks'
  const statusText = updating && background ? `${baseStatus} (${background.TasksQtyToProcess} ${taskLabel})` : baseStatus
  const timeText = background ? `as of ${dateFormat(background.UpdatedAt, Format['HH MM'])}` : '\u00A0'

  return (
    <Paper
      variant='outlined'
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        mt: '-25px',
        py: 1.5,
        border: '1px solid #d7d7d7',
        borderLeft: isLoading ? '1px solid ##d7d7d' : `3px solid ${updating ? 'var(--warning, #f59e0b)' : 'var(--success, #22c55e)'}`,
        borderRadius: 1,
        bgcolor: 'var(--primary200)',
        minWidth: 280,
      }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, height: '2.2rem' }}>
          <Preloader size='xs' />
        </Box>
      ) : (
        <>
          <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {renderIcon()}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, height: '2.2rem', justifyContent: 'center' }}>
            <span style={{ fontSize: '16px', fontWeight: 400, lineHeight: 1.5, letterSpacing: 0, whiteSpace: 'nowrap' }}>{statusText}</span>
            <span style={{ lineHeight: 1.3 }}>{timeText}</span>
          </Box>
        </>
      )}
      {!isLoading && (
        <IconButton
          size='small'
          onClick={() => fetchBackground(true)}
          sx={{ flexShrink: 0 }}
        >
          <RefreshIcon fontSize='small' />
        </IconButton>
      )}
    </Paper>
  )
}
